import React from "react";
import { getProfileName, ThemeWrapper, ViewDetailsModal } from "@verity/components";
import { CustomSettingData } from "../../settings-page";
import apiClient from "../../../../helpers/api-client";
import i18n from "../../../../i18n";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { ExtraSettingsInfo } from "../../../webpages/settings-root";
import { isEnforcedProfile } from "@verity/proctoring-option";
import { ProfileTable } from "../shared/profile-table";
import { ProfileSearchSelect } from "../shared/profile-search-select";
import { CoreButton, CoreRadioGroup, CoreSpinner, RadioOption } from "@verity/components/core";
import { UseFetchProctoringDataReturn } from "../useFetchProctoringData";

export type ProfileEnforceEdits = { enforceList: Set<number>; unenforceList: Set<number> };

export const ApprovedProfilesInput = (
	props: {
		setting: CustomSettingData<ProfileEnforceEdits>;
		settingsInfo?: Pick<
			Partial<ExtraSettingsInfo>,
			"Info-instituteProfiles" | "Info-instituteSettings" | "Info-optionRelatedInstituteSettings"
		>;
		onSettingChange: (value?: ProfileEnforceEdits) => void;
		disabled?: boolean;
		ariaLabelledByTitleId?: string;
	} & UseFetchProctoringDataReturn,
) => {
	const [selectedProfile, setSelectedProfile] = React.useState<ProctoringOptionProfileV2>();
	const [searchInput, setSearchInput] = React.useState("");

	const [selectedDropdownProfile, setSelectedDropdownProfile] = React.useState<ProctoringOptionProfileV2>();

	const [viewProfileModalOpen, setViewProfileModalOpen] = React.useState(false);

	const [profilesEdits, setProfilesEdits] = React.useState<ProfileEnforceEdits>(
		props.setting.value ?? { enforceList: new Set(), unenforceList: new Set() },
	);
	const [profiles, setProfiles] = React.useState<ProctoringOptionProfileV2[]>(
		applyEditsToProfilesList(props.settingsInfo?.["Info-instituteProfiles"] ?? [], profilesEdits),
	);

	const enforcedProfiles = profiles
		.filter((x) => isEnforcedProfile(x))
		.sort((a, b) => (a.name ?? "").toLocaleLowerCase().localeCompare((b.name ?? "").toLowerCase()));

	const unenforcedProfiles = profiles
		.filter((x) => !isEnforcedProfile(x) && (x.name ?? "").toLowerCase().includes(searchInput.toLowerCase()))
		.sort((a, b) => (a.name ?? "").toLocaleLowerCase().localeCompare((b.name ?? "").toLowerCase()));

	const [enforcedProfilesEnabled, setEnforcedProfilesEnabled] = React.useState(enforcedProfiles.length > 0);

	React.useEffect(() => {
		if (!enforcedProfilesEnabled) {
			const enforceList = profilesEdits.enforceList;
			const unenforceList = profilesEdits.unenforceList;
			profiles.forEach((p) => {
				enforceList.delete(p.id);
				if (p.instituteProfile?.enforced) {
					unenforceList.add(p.id);
				}
			});
			setProfiles(
				applyEditsToProfilesList(props.settingsInfo?.["Info-instituteProfiles"] ?? [], {
					enforceList: enforceList,
					unenforceList: unenforceList,
				}),
			);
			setProfilesEdits({ enforceList: enforceList, unenforceList: unenforceList });
			props.onSettingChange({ enforceList: enforceList, unenforceList: unenforceList });
		}
	}, [enforcedProfilesEnabled, profiles, profilesEdits.enforceList, profilesEdits.unenforceList]);

	function applyEditsToProfilesList(profilesList: ProctoringOptionProfileV2[], profilesEdits?: ProfileEnforceEdits) {
		if (!profilesEdits) {
			return profilesList;
		}
		const idToIndex = new Map<number, number>(profilesList.map((x, i) => [x.id, i]));
		for (const id of profilesEdits.enforceList) {
			const index = idToIndex.get(id);
			if (index !== undefined && profilesList[index].instituteProfile) {
				const instituteProfile = profilesList[index].instituteProfile;
				if (instituteProfile) {
					instituteProfile.enforced = true;
				}
			}
		}
		for (const id of profilesEdits.unenforceList) {
			const index = idToIndex.get(id);
			if (index !== undefined && profilesList[index].instituteProfile) {
				const instituteProfile = profilesList[index].instituteProfile;
				if (instituteProfile) {
					instituteProfile.enforced = false;
				}
			}
		}
		return profilesList;
	}

	function editProfile(profile: ProctoringOptionProfileV2, enforce: boolean) {
		const enforceList = profilesEdits.enforceList;
		const unenforceList = profilesEdits.unenforceList;

		if (enforce) {
			enforceList.add(profile.id);
			unenforceList.delete(profile.id);
		} else {
			unenforceList.add(profile.id);
			enforceList.delete(profile.id);
		}

		setProfiles(
			applyEditsToProfilesList(props.settingsInfo?.["Info-instituteProfiles"] ?? [], {
				enforceList: enforceList,
				unenforceList: unenforceList,
			}),
		);
		setProfilesEdits({ enforceList: enforceList, unenforceList: unenforceList });
		props.onSettingChange({ enforceList: enforceList, unenforceList: unenforceList });
	}

	const optionRelatedInstituteSettings = props.settingsInfo?.["Info-optionRelatedInstituteSettings"];

	const verityInstituteSettings = props.settingsInfo?.["Info-instituteSettings"];

	if (!verityInstituteSettings) {
		return <CoreSpinner />;
	}

	const radioOptions: RadioOption[] = ["enable", "disable"].map((value) => ({
		value,
		label: value.charAt(0).toUpperCase() + value.slice(1),
		disabled: props.disabled,
	}));

	return (
		<ThemeWrapper>
			<div
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					gap: "28px",
				}}
			>
				<CoreRadioGroup
					radioGroupAriaLabelledby={props.ariaLabelledByTitleId}
					radioOptions={radioOptions}
					radioLabelFontSize={14}
					coreRadioSize={16}
					onChange={(e) => setEnforcedProfilesEnabled(e.target.value === "enable")}
					formControlLabelProps={{
						sx: {
							marginLeft: 0,
						},
					}}
					radioProps={{
						sx: {
							padding: "2px",
							marginRight: "4px",
						},
					}}
					selectedValue={enforcedProfilesEnabled ? "enable" : "disable"}
				/>
			</div>

			{enforcedProfilesEnabled && (
				<>
					<div
						style={{
							width: "100%",
							display: "flex",
							alignItems: "center",
							placeContent: "center",
							border: "1px solid #e4e4e4",
							borderRadius: "8px",
							padding: "8px 24px",
							gap: "12px",
							margin: "10px 0 14px 0",
						}}
					>
						<ProfileSearchSelect
							profiles={unenforcedProfiles}
							selectedProfile={selectedProfile}
							setSelectedProfile={setSelectedProfile}
							searchInput={searchInput}
							setSearchInput={setSearchInput}
							onClickSelect={(p) => {
								if (p) {
									editProfile(p, true);
									setSearchInput("");
									setSelectedProfile(undefined);
								}
							}}
						/>
						<CoreButton
							onClick={() => {
								let selected = selectedProfile;
								if (searchInput !== "" && (selectedProfile?.name ?? "")?.toLowerCase() !== searchInput.toLowerCase()) {
									selected = unenforcedProfiles.find(
										(x) => searchInput !== "" && x.name?.toLowerCase() === searchInput.toLowerCase(),
									);
								}
								if (selected) {
									editProfile(selected, true);
									setSelectedProfile(undefined);
								}
							}}
						>
							{i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.add")}
						</CoreButton>
					</div>
					<ProfileTable
						displayTable={enforcedProfiles.length > 0}
						profiles={enforcedProfiles}
						emptyTableHeaderText={i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.noInstProfile")}
						dropdownOptions={[
							{
								name: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.view"),
								onClick: (profile) => {
									setSelectedDropdownProfile(profile);
									setViewProfileModalOpen(true);
								},
							},
							{
								name: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.remove"),
								onClick: (profile) => {
									editProfile(profile, false);
								},
							},
						]}
						verityInstituteSettings={verityInstituteSettings}
					/>
				</>
			)}

			{viewProfileModalOpen && selectedDropdownProfile && (
				<ViewDetailsModal
					profile={selectedDropdownProfile}
					title={i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.proctorProfile", {
						profile: getProfileName(selectedDropdownProfile),
					})}
					configuration={{
						optionRelatedInstituteSettings,
						verityInstituteSettings,
						proctoringData: { applications: props.applications, extensions: props.extensions },
						proctoringOption: selectedDropdownProfile.proctoringOption,
					}}
					onClose={() => {
						setViewProfileModalOpen(false);
						setSelectedProfile(undefined);
					}}
				/>
			)}
		</ThemeWrapper>
	);
};

export async function saveProfileEnforce(profilesEdits?: ProfileEnforceEdits) {
	try {
		if (profilesEdits) {
			await apiClient.proctoringProfile.enforce({
				enforceProfileIds: Array.from(profilesEdits.enforceList.values()),
				unenforceProfileIds: Array.from(profilesEdits.unenforceList.values()),
			});
		}
		return { ok: true };
	} catch (error) {
		console.error(error);
		return { ok: false, reason: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.notSave") };
	}
}
